/**
 * App controller — boots the data layer, then runs a small screen state machine:
 *   loading -> home -> play(question -> reveal) -> summary -> home
 */
import { openClueDb } from './data/db';
import { ClueRepo } from './data/repo';
import { ProgressStore } from './data/progress';
import { Session } from './engine/session';
import { rankWeakTags } from './engine/selection';
import { modernValue } from './engine/value';
import { applyFits, installRefit } from './ui/fit';
import { Grade, type Board, type Clue, type PracticeMode } from './engine/types';
import {
  boardClueView,
  boardRevealView,
  boardSummaryView,
  boardView,
  categoryIntroView,
  categoryPickerView,
  focusPickerView,
  homeView,
  loadingView,
  playView,
  revealView,
  summaryView,
  topicPickerView,
  type BoardState,
} from './ui/views';

const appEl = document.getElementById('app')!;

function mount(view: HTMLElement): void {
  appEl.replaceChildren(view);
  // Fit clue/category/value text to their boxes once layout is settled, then
  // again after the display fonts finish loading (their metrics differ).
  requestAnimationFrame(() => applyFits(appEl));
  document.fonts?.ready.then(() => applyFits(appEl));
}

async function boot(): Promise<void> {
  const loading = loadingView();
  mount(loading.root);

  installRefit(appEl);
  const db = await openClueDb((loaded, total, phase) => loading.set(total ? loaded / total : 0, phase));
  const repo = new ClueRepo(db);
  const progress = new ProgressStore();
  await progress.open();

  const tags = repo.getTags();
  const tagById = new Map(tags.map((t) => [t.id, t]));
  const allTagIds = tags.map((t) => t.id);
  const clueCount = repo.clueCount();

  const showHome = () => {
    mount(
      homeView({
        clueCount,
        lifetime: progress.stats,
        onAdaptive: () => startAdaptive(),
        onBoard: () => startBoard(),
        onCategory: () => showCategoryPicker(),
        onTopic: () => showTopicPicker(),
        onFocus: () => showFocusPicker(),
        onReset: async () => {
          if (confirm('Erase all practice progress and skill estimates?')) {
            await progress.reset();
            showHome();
          }
        },
      }),
    );
  };

  function modeLabel(mode: PracticeMode): string {
    if (mode.kind === 'adaptive') return 'Adaptive';
    if (mode.kind === 'category') return mode.title;
    if (mode.kind === 'deck') return mode.name;
    return tagById.get(mode.tagId)?.name ?? 'Category';
  }

  function showFocusPicker(): void {
    mount(
      focusPickerView({
        decks: repo.getDecks(),
        onPick: (deck) => startSession({ kind: 'deck', deckId: deck.id, name: deck.name }),
        onBack: showHome,
      }),
    );
  }

  function showTopicPicker(): void {
    mount(
      topicPickerView({
        tags,
        skills: progress.skills,
        onPick: (tagId) => startSession({ kind: 'tag', tagId }),
        onBack: showHome,
      }),
    );
  }

  function showCategoryPicker(): void {
    mount(
      categoryPickerView({
        initial: repo.searchCategories(''),
        onSearch: (q) => repo.searchCategories(q),
        onPick: (title) => startSession({ kind: 'category', title }),
        onBack: showHome,
      }),
    );
  }

  function startSession(mode: PracticeMode): void {
    const session = new Session(repo, progress, mode, allTagIds);
    const label = modeLabel(mode);

    const advance = () => {
      const clue = session.nextClue();
      if (!clue) return showSummary(session);
      showQuestion(session, label, clue);
    };

    const showQuestion = (s: Session, lbl: string, clue: Clue) => {
      mount(
        playView(clue, lbl, s.stats, {
          onReveal: () => showReveal(s, lbl, clue),
          onGrade: () => {},
          onQuit: () => showSummary(s),
        }),
      );
    };

    const showReveal = (s: Session, lbl: string, clue: Clue) => {
      mount(
        revealView(clue, lbl, s.stats, {
          onReveal: () => {},
          onGrade: async (g: Grade) => {
            await s.grade(clue, g);
            advance();
          },
          onQuit: () => showSummary(s),
        }),
      );
    };

    advance();
  }

  /**
   * Adaptive practice = a weak-tag-biased *category* at a time. We pick a real
   * 5-clue category column (one that contains a clue from a weak tag), present
   * its five clues in ascending value, grade each (feeding FSRS + tag skills),
   * then pick the next category.
   */
  function startAdaptive(): void {
    const grader = new Session(repo, progress, { kind: 'adaptive' }, allTagIds);
    const played = new Set<string>();

    const nextCategory = () => {
      const weak = rankWeakTags(progress.skills, 3)
        .slice(0, 6)
        .map((t) => t.tagId);
      const cat = repo.randomAdaptiveCategory(weak, Math.random, played);
      if (!cat) return showAdaptiveSummary();
      played.add(cat.name);
      mount(
        categoryIntroView({
          name: cat.name,
          stats: grader.stats,
          onStart: () => playClue(cat, 0),
          onQuit: showAdaptiveSummary,
        }),
      );
    };

    const label = (idx: number) => `Clue ${idx + 1} of 5`;

    const playClue = (cat: { name: string; clues: Clue[] }, idx: number) => {
      if (idx >= cat.clues.length) return nextCategory();
      const clue = cat.clues[idx]!;
      mount(
        playView(clue, label(idx), grader.stats, {
          onReveal: () => revealClue(cat, idx, clue),
          onGrade: () => {},
          onQuit: showAdaptiveSummary,
        }),
      );
    };

    const revealClue = (cat: { name: string; clues: Clue[] }, idx: number, clue: Clue) => {
      mount(
        revealView(clue, label(idx), grader.stats, {
          onReveal: () => {},
          onGrade: async (g: Grade) => {
            await grader.grade(clue, g);
            playClue(cat, idx + 1);
          },
          onQuit: showAdaptiveSummary,
        }),
      );
    };

    const showAdaptiveSummary = () => {
      const weak = rankWeakTags(progress.skills, 3)
        .slice(0, 5)
        .map((w) => ({ name: tagById.get(w.tagId)?.name ?? '?', rating: w.rating }));
      mount(
        summaryView({
          stats: grader.stats,
          weak,
          onHome: showHome,
          onAgain: startAdaptive,
        }),
      );
    };

    nextCategory();
  }

  function startBoard(): void {
    const board: Board | null = repo.randomBoard();
    if (!board) return showHome();
    const state: BoardState = { used: new Set(), score: 0 };
    const total = board.categories.reduce((n, c) => n + c.clues.length, 0);
    const maxScore = board.categories.reduce(
      (s, c) => s + c.clues.reduce((a, cl) => a + modernValue(cl), 0),
      0,
    );
    let correct = 0;
    // Reuse the session grader so board play still feeds FSRS scheduling + tag
    // skills (the mode is irrelevant here — only grade() is used).
    const grader = new Session(repo, progress, { kind: 'adaptive' }, allTagIds);

    const showBoard = () => {
      if (state.used.size >= total) return showBoardSummary();
      mount(boardView(board, state, { onCell: playCell, onQuit: showBoardSummary }));
    };
    const playCell = (clue: Clue) => {
      const value = modernValue(clue);
      mount(
        boardClueView(clue, value, state.score, {
          onReveal: () => revealCell(clue, value),
          onQuit: showBoard,
        }),
      );
    };
    const revealCell = (clue: Clue, value: number) => {
      mount(
        boardRevealView(clue, value, state.score, {
          onGrade: async (g: Grade, isCorrect: boolean) => {
            await grader.grade(clue, g);
            state.used.add(clue.i);
            state.score += isCorrect ? value : -value;
            if (isCorrect) correct++;
            showBoard();
          },
          onQuit: showBoard,
        }),
      );
    };
    const showBoardSummary = () =>
      mount(
        boardSummaryView({
          score: state.score,
          maxScore,
          date: board.date,
          correct,
          total,
          onAnother: startBoard,
          onHome: showHome,
        }),
      );

    showBoard();
  }

  function showSummary(session: Session): void {
    const weak = rankWeakTags(progress.skills, 3)
      .slice(0, 5)
      .map((w) => ({ name: tagById.get(w.tagId)?.name ?? '?', rating: w.rating }));
    mount(
      summaryView({
        stats: session.stats,
        weak,
        onHome: showHome,
        onAgain: () => startSession(session.mode),
      }),
    );
  }

  showHome();
}

boot().catch((err) => {
  const div = document.createElement('div');
  div.className = 'screen center';
  div.innerHTML = `<div class="title">Oops</div><p>Failed to load the clue bank.</p><pre style="font-size:11px;opacity:.7;max-width:90%;white-space:pre-wrap">${String(
    err,
  )}</pre>`;
  appEl.replaceChildren(div);
  console.error(err);
});
